import { DateTime } from 'luxon';

import { PATHS } from 'router/config';

import {
  MARKETING_SIGNAL_KINDS,
  marketingDelta,
  marketingNumber,
  marketingParseJson,
  MARKETING_METRICS,
  MarketingMetricKey,
  IMarketingDelta,
} from './marketingModel';
import {
  IMarketingDigest,
  IMarketingForecastRow,
  IMarketingGroupFill,
  IMarketingSignalRouting,
  IMarketingSnapshot,
  MarketingDate,
  MarketingDigestStatusCode,
} from './types';

/**
 * ТЗ 0088, этап 4 «Интеллект» — чистые помощники экранов: прогноз месяца по
 * курсу, заполнение ближайших групп, AI-разбор недели и его снимок, письма
 * SendGrid, маршрут «сигналы → задачи». Без React, Apollo и i18n (подписи —
 * ключами `marketing.*`), как и `marketingModel`.
 *
 * Правило раздела то же: **пропуск ≠ ноль**. P10 / P90 при малой базе,
 * план, заполнение группы без мест, доля открытий при нуле доставок — `null`,
 * и экран рисует прочерк, а не 0.
 */

/* ── Прогноз месяца ──────────────────────────────────────────────────── */

/** Этап лида без этапа Kommo — код сервера, не текст. */
export const MARKETING_FORECAST_UNKNOWN_STAGE = 'unknown';

/** Основа вероятности этапа: его наблюдения или общая конверсия курса. */
export const MARKETING_FORECAST_BASES = ['stage', 'overall'] as const;

export type MarketingForecastBasis = typeof MARKETING_FORECAST_BASES[number];

export const marketingForecastBasis = (
  value: string | null | undefined,
): MarketingForecastBasis | null =>
  value && (MARKETING_FORECAST_BASES as readonly string[]).includes(value)
    ? (value as MarketingForecastBasis)
    : null;

export type MarketingForecastTone = 'ok' | 'warn' | 'danger' | 'neutral';

/**
 * Прогноз против плана: P10 или P50 дотягивают до плана — `ok`; план
 * достижим только в хорошем сценарии (P90) — `warn`; не достаётся и в нём —
 * `danger`. Плана нет — `neutral` (сравнивать не с чем).
 */
export const marketingForecastTone = (
  row: Pick<IMarketingForecastRow, 'p10' | 'p50' | 'p90' | 'target'>,
): MarketingForecastTone => {
  const target = marketingNumber(row.target);
  if (target === null || target <= 0) return 'neutral';
  if (row.p50 >= target) return 'ok';
  const p90 = marketingNumber(row.p90);
  if (p90 !== null && p90 >= target) return 'warn';
  return 'danger';
};

export interface IMarketingForecastScale {
  /** Верх шкалы (штук продаж) — чуть выше максимума из P90, P50 и плана. */
  max: number;
  /** Доли шкалы 0..100; `null` — значения нет (малая база, нет плана). */
  mtd: number;
  p10: number | null;
  p50: number;
  p90: number | null;
  target: number | null;
}

const pctOf = (value: number, max: number): number =>
  Math.max(0, Math.min(100, (value / max) * 100));

/**
 * Геометрия полосы «P10–P90 против плана»: продано с 1-го — залитая часть,
 * диапазон — полоса, P50 — отметка, план — вертикальная черта. Верх шкалы —
 * максимум + 15 %: отметка на самом краю читалась бы как «упёрлось».
 */
export const marketingForecastScale = (
  row: Pick<
    IMarketingForecastRow,
    'salesMtd' | 'p10' | 'p50' | 'p90' | 'target'
  >,
): IMarketingForecastScale => {
  const p10 = marketingNumber(row.p10);
  const p90 = marketingNumber(row.p90);
  const target = marketingNumber(row.target);
  const top = Math.max(row.p50, p90 ?? 0, target ?? 0, row.salesMtd, 1);
  const max = top * 1.15;
  return {
    max,
    mtd: pctOf(row.salesMtd, max),
    p10: p10 === null ? null : pctOf(p10, max),
    p50: pctOf(row.p50, max),
    p90: p90 === null ? null : pctOf(p90, max),
    target: target === null || target <= 0 ? null : pctOf(target, max),
  };
};

/** Диапазон сузился в точку (прошедший месяц — факт, или всё уже продано). */
export const marketingForecastIsPoint = (
  row: Pick<IMarketingForecastRow, 'p10' | 'p50' | 'p90'>,
): boolean => row.p10 === row.p50 && row.p90 === row.p50;

export type MarketingGroupFillState = 'closed' | 'noSeats' | 'fill';

export interface IMarketingGroupFillView {
  state: MarketingGroupFillState;
  /** Заполнение P50 долей 0..1 для полосы (обрезано по 100 %). */
  bar: number | null;
  /** Тон общей палитры: риск недобора — `danger`, иначе `ok`. */
  tone: 'ok' | 'danger' | 'neutral';
}

/**
 * Заполнение группы: 0 мест — набор закрыт (заполнения и риска нет), мест
 * не задано — не с чем сравнить; иначе полоса P50 и тон по риску сервера
 * (< 60 % мест).
 */
export const marketingGroupFillView = (
  group: Pick<IMarketingGroupFill, 'seats' | 'fillP50' | 'risk'>,
): IMarketingGroupFillView => {
  if (group.seats === 0) return { state: 'closed', bar: null, tone: 'neutral' };
  if (group.seats === null || group.seats === undefined) {
    return { state: 'noSeats', bar: null, tone: 'neutral' };
  }
  const fill = marketingNumber(group.fillP50);
  return {
    state: 'fill',
    bar: fill === null ? null : Math.max(0, Math.min(1, fill)),
    tone: group.risk ? 'danger' : 'ok',
  };
};

/**
 * Карточка группы — только из `PATHS` (группа живёт по
 * `/education/groups/:id/details`, до ТЗ 0093 — `/settings/…`; путь «по
 * аналогии» дал бы 404).
 */
export const marketingGroupCardHref = (groupId: string): string =>
  PATHS.groupDetail.replace(':id', groupId);

/* ── Недели ──────────────────────────────────────────────────────────── */

const ISO_WEEK_RE = /^(\d{4})-W(\d{2})$/;

/** Понедельник и воскресенье ISO-недели `2026-W39`; мусор — `null`. */
export const marketingIsoWeekRange = (
  isoWeek: string,
): { from: MarketingDate; to: MarketingDate } | null => {
  const match = ISO_WEEK_RE.exec(isoWeek);
  if (!match) return null;
  const monday = DateTime.fromISO(`${match[1]}-W${match[2]}-1`, {
    zone: 'utc',
  });
  if (!monday.isValid) return null;
  return {
    from: monday.toISODate(),
    to: monday.plus({ days: 6 }).toISODate(),
  };
};

/** Последняя ЗАВЕРШЁННАЯ ISO-неделя на день `today` (по Варшаве). */
export const marketingLastCompletedWeek = (today: MarketingDate): string => {
  const previous = DateTime.fromISO(today, { zone: 'utc' }).minus({ days: 7 });
  return `${previous.weekYear}-W${String(previous.weekNumber).padStart(
    2,
    '0',
  )}`;
};

/**
 * Неделя одной строкой: «21–27.09», через месяц — «28.09–04.10», не
 * текущий год — с годом: «29.12.25–04.01.26».
 */
export const marketingWeekLabel = (
  range: { from: MarketingDate; to: MarketingDate },
  currentYear: number = new Date().getFullYear(),
): string => {
  const from = DateTime.fromISO(range.from, { zone: 'utc' });
  const to = DateTime.fromISO(range.to, { zone: 'utc' });
  if (!from.isValid || !to.isValid) return `${range.from}–${range.to}`;
  if (from.year !== currentYear || to.year !== currentYear) {
    return `${from.toFormat('dd.LL.yy')}–${to.toFormat('dd.LL.yy')}`;
  }
  if (from.month === to.month) {
    return `${from.toFormat('dd')}–${to.toFormat('dd.LL')}`;
  }
  return `${from.toFormat('dd.LL')}–${to.toFormat('dd.LL')}`;
};

/* ── AI-разбор ───────────────────────────────────────────────────────── */

export const MARKETING_DIGEST_STATUSES: MarketingDigestStatusCode[] = [
  'queued',
  'running',
  'published',
  'rejected',
  'failed',
];

export const isMarketingDigestStatus = (
  value: string | null | undefined,
): value is MarketingDigestStatusCode =>
  Boolean(value) &&
  (MARKETING_DIGEST_STATUSES as string[]).includes(value as string);

/** Сводка ещё готовится — экран опрашивает её статус. */
export const marketingDigestActive = (status: string): boolean =>
  status === 'queued' || status === 'running';

/** Тон статуса сводки — общая палитра `L.Tag`. */
export const MARKETING_DIGEST_TONES: Readonly<
  Record<MarketingDigestStatusCode, 'ok' | 'warn' | 'danger' | 'info'>
> = {
  queued: 'info',
  running: 'info',
  published: 'ok',
  rejected: 'warn',
  failed: 'danger',
};

export interface IMarketingDigestWeek {
  isoWeek: string;
  from: MarketingDate | null;
  to: MarketingDate | null;
  /** Самая новая попытка недели (любого статуса); `null` — сводок нет. */
  latest: IMarketingDigest | null;
  /** Самая новая опубликованная; `null` — опубликованной нет. */
  published: IMarketingDigest | null;
  /** Есть снимок недели (факты для сводки заморожены). */
  hasSnapshot: boolean;
}

/**
 * Недели истории: из сводок (новые сверху) и снимков. Неделя без сводки, но
 * со снимком, тоже в списке — её можно собрать. Новые недели сверху.
 */
export const marketingDigestWeeks = (
  digests: ReadonlyArray<IMarketingDigest>,
  snapshots: ReadonlyArray<IMarketingSnapshot>,
): IMarketingDigestWeek[] => {
  const byWeek: Record<string, IMarketingDigestWeek> = {};
  const touch = (isoWeek: string): IMarketingDigestWeek => {
    if (!byWeek[isoWeek]) {
      const range = marketingIsoWeekRange(isoWeek);
      byWeek[isoWeek] = {
        isoWeek,
        from: range?.from ?? null,
        to: range?.to ?? null,
        latest: null,
        published: null,
        hasSnapshot: false,
      };
    }
    return byWeek[isoWeek];
  };
  snapshots.forEach((snapshot) => {
    const week = touch(snapshot.isoWeek);
    week.hasSnapshot = true;
    week.from = snapshot.dateFrom;
    week.to = snapshot.dateTo;
  });
  const newest = [...digests].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt),
  );
  newest.forEach((digest) => {
    const week = touch(digest.isoWeek);
    if (!week.latest) week.latest = digest;
    if (!week.published && digest.status === 'published') {
      week.published = digest;
    }
  });
  return Object.values(byWeek).sort((a, b) =>
    b.isoWeek.localeCompare(a.isoWeek),
  );
};

export interface IMarketingDigestPick {
  /** Самая новая попытка недели. */
  latest: IMarketingDigest | null;
  /**
   * Что показывать текстом: самая новая ОПУБЛИКОВАННАЯ. Пересборка поверх
   * неё (готовится, отклонена, сбой) не прячет прошлый текст.
   */
  shown: IMarketingDigest | null;
}

export const marketingPickDigest = (
  digests: ReadonlyArray<IMarketingDigest>,
): IMarketingDigestPick => {
  const newest = [...digests].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt),
  );
  return {
    latest: newest[0] ?? null,
    shown:
      newest.find(
        (digest) => digest.status === 'published' && Boolean(digest.text),
      ) ?? null,
  };
};

/**
 * Адрес ссылки в тексте сводки: только свои пути ERP (`/marketing?…`) и
 * http(s). `javascript:`, `data:`, протокол-относительные `//хост` и прочее —
 * `null`: ссылка рисуется простым текстом.
 */
export const marketingSafeDigestHref = (
  href: string | null | undefined,
): string | null => {
  const value = (href ?? '').trim();
  if (!value) return null;
  if (value.startsWith('/') && !value.startsWith('//')) return value;
  return /^https?:\/\//i.test(value) ? value : null;
};

/* ── Разделы текста сводки ───────────────────────────────────────────── */

/**
 * Раздел текста сводки: заголовок второго уровня (`## Коротко`) и всё, что
 * под ним до следующего такого же. Текст до первого заголовка — раздел без
 * заголовка (`title: ''`).
 */
export interface IMarketingDigestSection {
  /** Якорь карточки раздела: `marketing-digest-1`, `-2`… — по порядку. */
  id: string;
  /** Заголовок без решёток и лишних пробелов; `''` — вступление. */
  title: string;
  /** Markdown раздела без строки заголовка и пустых строк по краям. */
  body: string;
}

/** Префикс якорей разделов (он же — id элементов карточек). */
export const MARKETING_DIGEST_SECTION_PREFIX = 'marketing-digest';

/**
 * `## Заголовок` по CommonMark: до трёх пробелов отступа, ровно две решётки,
 * пробел; закрывающие решётки (`## Коротко ##`) — только после пробела, чтобы
 * «C#» в названии не терялся. `#` и `###` сюда не попадают — они остаются
 * внутри раздела.
 */
const DIGEST_H2_RE = /^ {0,3}##[ \t]+(\S.*?)(?:[ \t]+#+)?[ \t]*$/;

/** Забор кода: заголовок внутри ```…``` — текст, а не раздел. */
const DIGEST_FENCE_RE = /^ {0,3}(`{3,}|~{3,})/;

/** Пустые строки по краям — прочь; отступ первой строки (код) — на месте. */
const trimBlankLines = (lines: ReadonlyArray<string>): string => {
  let start = 0;
  let end = lines.length;
  while (start < end && !lines[start].trim()) start += 1;
  while (end > start && !lines[end - 1].trim()) end -= 1;
  return lines.slice(start, end).join('\n');
};

/**
 * Режет markdown сводки на разделы по заголовкам второго уровня. Переводы
 * строк `\r\n` / `\r` — как `\n`; пробелы вокруг заголовка не мешают;
 * `#` и `###` — не разрез; заголовок внутри блока кода — не разрез. Пустое
 * вступление не возвращается, пустой раздел с заголовком — возвращается
 * (заголовок модели — тоже содержание).
 */
export const splitMarketingDigestSections = (
  text: string | null | undefined,
): IMarketingDigestSection[] => {
  const lines = (text ?? '').replace(/\r\n?/g, '\n').split('\n');
  const raw: { title: string; lines: string[] }[] = [{ title: '', lines: [] }];
  let fence: string | null = null;
  lines.forEach((line) => {
    const fenceMatch = DIGEST_FENCE_RE.exec(line);
    if (fenceMatch) {
      const [, marker] = fenceMatch;
      if (fence === null) fence = marker.charAt(0);
      else if (marker.charAt(0) === fence) fence = null;
    }
    const heading = fence === null && !fenceMatch && DIGEST_H2_RE.exec(line);
    if (heading) {
      raw.push({ title: heading[1].trim(), lines: [] });
    } else {
      raw[raw.length - 1].lines.push(line);
    }
  });
  return raw
    .map((section) => ({
      title: section.title,
      body: trimBlankLines(section.lines),
    }))
    .filter((section, index) => index > 0 || section.body !== '')
    .map((section, index) => ({
      id: `${MARKETING_DIGEST_SECTION_PREFIX}-${index + 1}`,
      ...section,
    }));
};

/**
 * Виды разделов: известные заголовки сводки (промпт сервера) рисуются
 * каждый по-своему; незнакомый — обычной карточкой (`other`), текст до
 * первого заголовка — `intro`.
 */
export const MARKETING_DIGEST_SECTION_KINDS = [
  'intro',
  'summary',
  'decisions',
  'changes',
  'watch',
  'unknowns',
  'other',
] as const;

export type MarketingDigestSectionKind = typeof MARKETING_DIGEST_SECTION_KINDS[number];

/** Заголовки промпта (по-русски: сводку модель пишет по-русски). */
const DIGEST_SECTION_TITLES: Readonly<
  Record<string, MarketingDigestSectionKind>
> = {
  коротко: 'summary',
  'решения недели': 'decisions',
  'что изменилось и почему': 'changes',
  'на что смотреть': 'watch',
  'чего не знаю': 'unknowns',
};

/** Заголовок для сравнения: без регистра, «ё» = «е», без точки/двоеточия. */
const normalizeDigestTitle = (title: string): string =>
  title
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[*_`]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/[\s.:!?…]+$/, '')
    .trim();

export const marketingDigestSectionKind = (
  title: string,
): MarketingDigestSectionKind => {
  if (!title.trim()) return 'intro';
  return DIGEST_SECTION_TITLES[normalizeDigestTitle(title)] ?? 'other';
};

/**
 * Пункт списка с короткой меткой («- Что сделать: связать…») → метка
 * жирным («- **Что сделать:** связать…»): в «Решениях недели» подпункт
 * читается строкой «метка — суть». Метка — до 40 знаков без разметки
 * (звёздочек, кода, скобок ссылки) и сразу за маркером; уже жирная метка
 * и строки внутри блока кода не трогаются. Меняется только разметка —
 * рендер тот же, безопасный.
 */
const DIGEST_LABEL_RE = /^(\s*(?:[-*+]|\d{1,3}[.)])[ \t]+)([^\s*_`[\]:#][^*_`[\]:\n]{0,39}):([ \t]+\S)/;

export const marketingDigestEmphasizeLabels = (body: string): string => {
  let fence: string | null = null;
  return body
    .split('\n')
    .map((line) => {
      const fenceMatch = DIGEST_FENCE_RE.exec(line);
      if (fenceMatch) {
        const marker = fenceMatch[1][0];
        if (fence === null) fence = marker;
        else if (marker === fence) fence = null;
        return line;
      }
      if (fence !== null) return line;
      return line.replace(
        DIGEST_LABEL_RE,
        (_all, marker: string, label: string, rest: string) =>
          `${marker}**${label.trimEnd()}:**${rest}`,
      );
    })
    .join('\n');
};

/* ── Снимок недели ───────────────────────────────────────────────────── */

/** Плитки снимка — те же восемь показателей, что у «Пульса». */
export const MARKETING_SNAPSHOT_KPIS: MarketingMetricKey[] = [
  'spendPln',
  'crmLeads',
  'sales',
  'revenuePln',
  'cashPln',
  'cplPln',
  'costPerSalePln',
  'roas',
];

/** Ключ показателя в JSON снимка (сервер пишет snake_case). */
const SNAPSHOT_JSON_KEYS: Readonly<
  Partial<Record<MarketingMetricKey, string>>
> = {
  spendPln: 'spend_pln',
  crmLeads: 'crm_leads',
  sales: 'sales',
  revenuePln: 'revenue_pln',
  cashPln: 'cash_pln',
  cplPln: 'cpl_pln',
  costPerSalePln: 'cost_per_sale_pln',
  roas: 'roas',
};

export type MarketingSnapshotValues = Partial<
  Record<MarketingMetricKey, number | null>
>;

export interface IMarketingSnapshotFacts {
  week: MarketingSnapshotValues;
  previous: MarketingSnapshotValues;
  signals: number | null;
  hypotheses: number | null;
  fatiguingAds: number | null;
  /** Все дни расхода недели покрыты; `null` — снимок не сказал. */
  spendComplete: boolean | null;
}

const snapshotValues = (raw: unknown): MarketingSnapshotValues => {
  const json = marketingParseJson(raw);
  return MARKETING_SNAPSHOT_KPIS.reduce<MarketingSnapshotValues>((acc, key) => {
    const name = SNAPSHOT_JSON_KEYS[key];
    const value = name ? json[name] : undefined;
    return {
      ...acc,
      [key]:
        typeof value === 'number' || typeof value === 'string'
          ? marketingNumber(value)
          : null,
    };
  }, {});
};

/** Сжатые факты снимка (`summary`) для полосы плиток «AI-разбора». */
export const marketingSnapshotFacts = (
  snapshot: Pick<IMarketingSnapshot, 'summary'>,
): IMarketingSnapshotFacts => {
  const json = marketingParseJson(snapshot.summary);
  const count = (key: string) => {
    const value = json[key];
    return typeof value === 'number' ? value : null;
  };
  return {
    week: snapshotValues(json.week),
    previous: snapshotValues(json.previous_week),
    signals: count('signals'),
    hypotheses: count('hypotheses'),
    fatiguingAds: count('fatiguing_ads'),
    spendComplete:
      typeof json.spend_complete === 'boolean' ? json.spend_complete : null,
  };
};

/** Дельта показателя снимка к прошлой неделе; нет любой стороны — `null`. */
export const marketingSnapshotDelta = (
  facts: IMarketingSnapshotFacts,
  key: MarketingMetricKey,
): IMarketingDelta | null =>
  marketingDelta(
    facts.week[key] ?? null,
    facts.previous[key] ?? null,
    MARKETING_METRICS[key].direction,
  );

/* ── Письма SendGrid ─────────────────────────────────────────────────── */

/**
 * Доля от доставленных (открытия, клики). Доставок 0 — `null`, а не 0 %:
 * рассылка ушла до окна, в окне у неё только поздние открытия.
 */
export const marketingEmailRate = (
  part: number | null | undefined,
  delivered: number | null | undefined,
): number | null => {
  const value = marketingNumber(part);
  const base = marketingNumber(delivered);
  if (value === null || base === null || base <= 0) return null;
  return value / base;
};

/* ── Маршрут «сигналы → задачи» ──────────────────────────────────────── */

/** Важность сигнала, не ниже которой он заводит задачу (по возрастанию). */
export const MARKETING_SIGNAL_SEVERITIES = [
  'info',
  'warning',
  'critical',
] as const;

export type MarketingSignalSeverity = typeof MARKETING_SIGNAL_SEVERITIES[number];

/** Сервер принимает не больше 20 исполнителей. */
export const MARKETING_ROUTING_MAX_ASSIGNEES = 20;

/**
 * Виды для выбора: известные экрану плюс сохранённые, которых экран не
 * знает, — иначе сохранение формы молча выкинуло бы их из правила.
 */
export const marketingRoutingKindOptions = (
  saved: ReadonlyArray<string>,
): string[] => [
  ...MARKETING_SIGNAL_KINDS,
  ...saved.filter(
    (kind) => !(MARKETING_SIGNAL_KINDS as readonly string[]).includes(kind),
  ),
];

/** Правило действует: есть и виды, и люди. */
export const marketingRoutingEnabled = (
  routing: Pick<IMarketingSignalRouting, 'kinds' | 'assigneeIds'>,
): boolean => routing.kinds.length > 0 && routing.assigneeIds.length > 0;

const sameSet = (a: ReadonlyArray<string>, b: ReadonlyArray<string>) =>
  a.length === b.length && a.every((item) => b.includes(item));

export const marketingSameRouting = (
  a: IMarketingSignalRouting,
  b: IMarketingSignalRouting,
): boolean =>
  a.minSeverity === b.minSeverity &&
  sameSet(a.kinds, b.kinds) &&
  sameSet(a.assigneeIds, b.assigneeIds);
